import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { BookingCard } from "@/components/BookingCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Loader2, Search, CalendarCheck, Clock, History, CarFront } from "lucide-react";

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/bookings"); setBookings(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upcoming = bookings.filter((b) => b.status === "accepted");
  const pending = bookings.filter((b) => b.status === "pending");
  const history = bookings.filter((b) => ["completed", "denied", "cancelled"].includes(b.status));

  const stats = [
    { label: "Upcoming lessons", value: upcoming.length, icon: CalendarCheck, color: "text-emerald-600 bg-emerald-50" },
    { label: "Pending requests", value: pending.length, icon: Clock, color: "text-amber-600 bg-amber-50" },
    { label: "Completed", value: bookings.filter((b) => b.status === "completed").length, icon: History, color: "text-sky-600 bg-sky-50" },
  ];

  const Empty = ({ text }) => (
    <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
      <CarFront className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p>{text}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-arctic-secondary tracking-tight" data-testid="dashboard-title">Hi, {user.name.split(" ")[0]} 👋</h1>
            <p className="text-muted-foreground mt-1">Here's your driving journey.</p>
          </div>
          <Button onClick={() => navigate("/instructors")} data-testid="find-instructors-btn" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90 h-11 px-6">
            <Search className="w-4 h-4 mr-2" /> Find instructors
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {stats.map((s) => (
            <Card key={s.label} className="p-5 rounded-2xl border-border flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
              <div>
                <div className="font-heading font-extrabold text-2xl text-arctic-secondary">{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            </Card>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div>
        ) : (
          <Tabs defaultValue="upcoming" className="mt-8">
            <TabsList data-testid="student-tabs">
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending ({pending.length})</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="upcoming" className="mt-6">
              {upcoming.length === 0 ? <Empty text="No upcoming lessons yet. Book one to get started!" /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{upcoming.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="student" onChange={load} />)}</div>}
            </TabsContent>
            <TabsContent value="pending" className="mt-6">
              {pending.length === 0 ? <Empty text="No pending requests." /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{pending.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="student" onChange={load} />)}</div>}
            </TabsContent>
            <TabsContent value="history" className="mt-6">
              {history.length === 0 ? <Empty text="No past lessons." /> :
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{history.map((b) => <BookingCard key={b.booking_id} booking={b} viewAs="student" onChange={load} />)}</div>}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
